import { createContext, useContext, useEffect, useReducer } from 'react'
import { useRouter } from 'next/router'
import { useMarketParam } from '../../hooks/use-market-param'

export type SortByState =
  | 'price_high_to_low'
  | 'price_low_to_high'
  | 'time_on_market_asc'
  | 'time_on_market_desc'
export type PriceState = {
  min: number | null
  max: number | null
}
export type BedsBathsState = {
  beds: number
  baths: number
}

type Action = { type: 'setCurrentState'; payload: State }

type State = {
  p: number
  priceMin: number | null
  priceMax: number | null
  address: string
  bedsMin: number
  bathsMin: number
  favorites: boolean | null
  sortByMethod: SortByState
  listing?: string
  market?: string
}

const defaultState: State = {
  p: 1,
  priceMin: null,
  priceMax: null,
  address: '',
  bedsMin: 0,
  bathsMin: 0,
  favorites: null,
  sortByMethod: 'time_on_market_asc',
}

type ContextValue = {
  state: State
  routePushPage: (page: number) => void
  routePushAddressFilter: (address: string) => void
  routePushBedBathFilter: (beds: number, baths: number) => void
  routePushPriceFilter: (min: number | null, max: number | null) => void
  routePushFavoritesFilter: (enabled: boolean | null) => void
  routePushSortMethod: (value: SortByState) => void
  routePushMarket: (value: string) => void
  routerResetAllFilters: () => void
  routerPushAllFilters: (
    bedsMin: number,
    bathsMin: number,
    priceMin: number | null,
    priceMax: number | null,
    address: string,
    favorites: boolean | null
  ) => void
  routePushIndex: () => void
}

const ListingsContext = createContext<ContextValue | undefined>(undefined)

const reducer = (state: State, action: Action) => {
  switch (action.type) {
    case 'setCurrentState': {
      return action.payload
    }
    default: {
      throw new Error(`Unhandled action type`)
    }
  }
}

export const getListingsStateForUrl = (state: State) => {
  return Object.fromEntries(
    Object.entries(state).filter(([param, value]) => {
      return value && defaultState[param as keyof State] !== value
    })
  )
}

export const getStateFromUrl = (query: object): State => {
  if (!query) {
    return defaultState
  }

  const validStateKeys = Object.keys(defaultState)
  const numberParams = ['bedsMin', 'bathsMin', 'p']
  return Object.fromEntries(
    Object.entries(query)
      .filter(([key]) => validStateKeys.includes(key))
      .map(([key, value]) =>
        numberParams.includes(key) ? [key, Number(value)] : [key, value]
      )
  ) as State
}

const ListingsProvider = ({ children }: { children: React.ReactNode }) => {
  const router = useRouter()
  const initial = { ...defaultState, ...getStateFromUrl(router.query) }
  const [state, dispatch] = useReducer(reducer, initial)
  const market = useMarketParam()

  const routePush = (query: State) => {
    router.push(
      {
        pathname: router.pathname,
        query: {
          state: market.split('/')[0],
          market: market.split('/')[1],
          ...getListingsStateForUrl(query),
        },
      },
      undefined,
      { shallow: true, scroll: false }
    )
  }

  const value: ContextValue = {
    state,
    routePushPage: (page) => {
      routePush({
        ...state,
        p: page,
      })
    },
    routePushBedBathFilter: (beds, baths) => {
      const resetPagination = state.bedsMin !== beds || state.bathsMin !== baths
      routePush({
        ...state,
        ...(resetPagination ? { p: 1 } : {}),
        bedsMin: beds,
        bathsMin: baths,
      })
    },
    routePushAddressFilter: (address) => {
      if (address === state.address) {
        return
      }

      routePush({
        ...state,
        p: 1,
        address: address,
      })
    },
    routePushPriceFilter: (min, max) => {
      const resetPagination = state.priceMin !== min || state.priceMax !== max
      routePush({
        ...state,
        ...(resetPagination ? { p: 1 } : {}),
        priceMin: min,
        priceMax: max,
      })
    },
    routePushFavoritesFilter: (enabled) => {
      const resetPagination = state.favorites !== enabled
      routePush({
        ...state,
        ...(resetPagination ? { p: 1 } : {}),
        favorites: enabled,
      })
    },
    routePushSortMethod: (value) => {
      routePush({
        ...state,
        sortByMethod: value,
      })
    },
    routerPushAllFilters: (
      bedsMin,
      bathsMin,
      priceMin,
      priceMax,
      address,
      favorites
    ) => {
      const resetPagination =
        state.bedsMin !== bedsMin ||
        state.bathsMin !== bathsMin ||
        state.priceMin !== priceMin ||
        state.priceMax !== priceMax ||
        state.address !== address ||
        state.favorites !== favorites
      routePush({
        ...state,
        ...(resetPagination ? { p: 1 } : {}),
        bedsMin: bedsMin,
        bathsMin: bathsMin,
        priceMin: priceMin,
        priceMax: priceMax,
        address: address,
        favorites: favorites,
      })
    },
    routerResetAllFilters: () => {
      routePush(defaultState)
    },
    routePushIndex: () => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { listing, ...rest } = router.query
      routePush(rest as any)
    },
    routePushMarket: (_market) => {
      const [state, market] = _market.split('/')

      routePush({
        state,
        market,
      } as any)
    },
  }

  useEffect(() => {
    const stateFromUrl = getStateFromUrl(router.query)

    dispatch({
      type: 'setCurrentState',
      payload: {
        ...defaultState,
        ...stateFromUrl,
      },
    })
  }, [router.query])

  return (
    <ListingsContext.Provider value={value}>
      {children}
    </ListingsContext.Provider>
  )
}

const useListingsState = () => {
  const context = useContext(ListingsContext)
  if (context === undefined) {
    throw new Error('useListingsState must be used within a ListingsProvider')
  }
  return context
}

export { ListingsProvider, useListingsState }
